<div class="signalr-status-container">
  <!-- Compact Status Display -->
  <div class="status-indicator" [class.expanded]="isExpanded" *ngIf="!showDetails">
    <div class="status-compact" (click)="toggleExpanded()">
      <i [class]="getConnectionStateIcon()"></i>
      <span class="status-text ms-2" [class]="getConnectionStateClass()">
        {{ getConnectionStateText() }}
      </span>
      <span *ngIf="messageCount > 0" class="badge bg-primary ms-2">{{ messageCount }}</span>
      <i class="fas fa-chevron-down toggle-icon" [class.rotated]="isExpanded"></i>
    </div>
    
    <!-- Expanded Quick Actions -->
    <div class="quick-actions" *ngIf="isExpanded">
      <div class="btn-group btn-group-sm">
        <button class="btn btn-outline-success" (click)="connect()" [disabled]="connectionStatus?.isConnected">
          <i class="fas fa-plug"></i>
          Connect
        </button>
        <button class="btn btn-outline-danger" (click)="disconnect()" [disabled]="!connectionStatus?.isConnected">
          <i class="fas fa-unplug"></i>
          Disconnect
        </button>
        <button class="btn btn-outline-warning" (click)="forceReconnect()">
          <i class="fas fa-redo"></i>
          Reconnect
        </button>
      </div>
    </div>
  </div>

  <!-- Detailed Status Display -->
  <div class="status-detailed" *ngIf="showDetails">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h6 class="mb-0">
          <i class="fas fa-broadcast-tower me-2"></i>
          SignalR Connection Status
        </h6>
        <div class="status-badge">
          <i [class]="getConnectionStateIcon()"></i>
          <span class="ms-2" [class]="getConnectionStateClass()">
            {{ getConnectionStateText() }}
          </span>
        </div>
      </div>
      
      <div class="card-body">
        <!-- Connection Information -->
        <div class="row mb-3" *ngIf="connectionStatus">
          <div class="col-md-6">
            <div class="info-item">
              <label class="form-label">Connection ID:</label>
              <div class="d-flex align-items-center">
                <code class="connection-id">
                  {{ connectionStatus.connectionId || 'Not connected' }}
                </code>
                <button 
                  class="btn btn-outline-secondary btn-sm ms-2"
                  *ngIf="connectionStatus.connectionId"
                  (click)="copyConnectionId()"
                  title="Copy Connection ID">
                  <i class="fas fa-copy"></i>
                </button>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="info-item">
              <label class="form-label">Reconnect Attempts:</label>
              <div>
                <span class="badge" [class.bg-warning]="connectionStatus.reconnectAttempts > 0" [class.bg-secondary]="connectionStatus.reconnectAttempts === 0">
                  {{ connectionStatus.reconnectAttempts }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-3" *ngIf="connectionStatus">
          <div class="col-md-6">
            <div class="info-item">
              <label class="form-label">Last Connected:</label>
              <div>
                <small class="text-muted">
                  {{ connectionStatus.lastConnected ? formatTimestamp(connectionStatus.lastConnected) : 'Never' }}
                  <span *ngIf="connectionStatus.lastConnected" class="text-info">
                    ({{ formatDuration(connectionStatus.lastConnected) }})
                  </span>
                </small>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="info-item">
              <label class="form-label">Last Disconnected:</label>
              <div>
                <small class="text-muted">
                  {{ connectionStatus.lastDisconnected ? formatTimestamp(connectionStatus.lastDisconnected) : 'Never' }}
                  <span *ngIf="connectionStatus.lastDisconnected" class="text-warning">
                    ({{ formatDuration(connectionStatus.lastDisconnected) }})
                  </span>
                </small>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Display -->
        <div class="alert alert-danger" *ngIf="connectionStatus?.error">
          <i class="fas fa-exclamation-triangle me-2"></i>
          <strong>Connection Error:</strong>
          <br>
          {{ connectionStatus?.error }}
        </div>

        <!-- Control Buttons -->
        <div class="btn-toolbar justify-content-between">
          <div class="btn-group">
            <button 
              class="btn btn-success" 
              (click)="connect()" 
              [disabled]="connectionStatus?.isConnected">
              <i class="fas fa-plug me-1"></i>
              Connect
            </button>
            <button 
              class="btn btn-danger" 
              (click)="disconnect()" 
              [disabled]="!connectionStatus?.isConnected">
              <i class="fas fa-unplug me-1"></i>
              Disconnect
            </button>
            <button 
              class="btn btn-warning" 
              (click)="forceReconnect()">
              <i class="fas fa-redo me-1"></i>
              Force Reconnect
            </button>
          </div>
          
          <!-- Test Buttons -->
          <div class="btn-group" *ngIf="connectionStatus?.isConnected">
            <button class="btn btn-outline-info btn-sm" (click)="sendTestMessage()">
              <i class="fas fa-paper-plane me-1"></i>
              Test Message
            </button>
            <button class="btn btn-outline-primary btn-sm" (click)="sendTestOrderUpdate()">
              <i class="fas fa-box me-1"></i>
              Test Order
            </button>
            <button class="btn btn-outline-warning btn-sm" (click)="sendTestSystemAlert()">
              <i class="fas fa-exclamation-triangle me-1"></i>
              Test Alert
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Message Log -->
  <div class="message-log mt-3" *ngIf="showMessageLog">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h6 class="mb-0">
          <i class="fas fa-list me-2"></i>
          Recent Messages
        </h6>
        <div class="btn-group btn-group-sm">
          <span class="badge bg-primary me-2">{{ messageCount }}</span>
          <button class="btn btn-outline-secondary" (click)="exportMessages()" [disabled]="recentMessages.length === 0">
            <i class="fas fa-download"></i>
          </button>
          <button class="btn btn-outline-danger" (click)="clearMessages()">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
      
      <div class="card-body p-0" *ngIf="recentMessages.length > 0">
        <div class="message-list">
          <div 
            class="message-item border-start border-3"
            [class]="getMessageTypeClass(message.type)"
            *ngFor="let message of recentMessages; let i = index">
            
            <div class="message-header">
              <div class="d-flex align-items-center">
                <i [class]="getMessageTypeIcon(message.type)"></i>
                <span class="message-type ms-2 fw-semibold">{{ message.type }}</span>
                <span class="timestamp ms-auto text-muted">{{ formatTimestamp(message.timestamp) }}</span>
              </div>
              <div *ngIf="message.user" class="message-user">
                <small class="text-muted">
                  <i class="fas fa-user me-1"></i>
                  {{ message.user }}
                </small>
              </div>
            </div>
            
            <div class="message-data">
              <pre class="code-block">{{ message.data | json }}</pre>
            </div>
          </div>
        </div>
      </div>
      
      <div class="card-body text-center text-muted" *ngIf="recentMessages.length === 0">
        <i class="fas fa-inbox fa-2x mb-2"></i>
        <p class="mb-0">No messages received yet</p>
        <small>Messages will appear here when received via SignalR</small>
      </div>
    </div>
  </div>
</div>